import { useEffect, useRef } from 'react'

// Re-seeds a block from its report-param section when the section moved outside the block (a
// revert, an undo, another view of the question, the URL).
//
// Keyed on the revision the shell bumps per host render as well as on the section. A block writes
// its own changes silently, so the section it was handed never moves with them -- and a revert back
// to the stored section lands on the very value props last carried, leaving nothing for a
// comparison of the section alone to notice. A host render is a fresh read of the live params; a
// render React starts for itself carries the revision it already had, so it never re-seeds.
//
// `reseed(section, previous)` runs on every host render, so it must keep the state it already has
// when the section resolves to it -- otherwise the block would announce, and write back, a change
// on each one.
export default function useParamsReseed(section, paramsRevision, reseed) {
  const sectionKey = JSON.stringify(section ?? null)
  const last = useRef({ sectionKey, paramsRevision })

  const reseedRef = useRef(reseed)
  reseedRef.current = reseed

  useEffect(() => {
    const previous = last.current
    if (previous.sectionKey === sectionKey && previous.paramsRevision === paramsRevision) return
    last.current = { sectionKey, paramsRevision }

    reseedRef.current(JSON.parse(sectionKey), JSON.parse(previous.sectionKey))
  }, [sectionKey, paramsRevision])
}

// Whether `next` would change nothing in `current`: every value it carries is already there.
export const isUnchanged = (current, next) =>
  Object.keys(next).every(key => JSON.stringify(next[key]) === JSON.stringify(current[key]))
